import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Search, Edit2, Trash2, RefreshCw, Truck, X, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const emptyForm = {
  name: '', contact_person: '', contact_phone: '', address: '', status: 'active', ewb_transporter_id: '',
  billing_mode: 'per_kg', kg_per_box: '', default_rate: '',
  states: [], category_rates: [], customers: [],
};

const TRANS_ID_RX = /^\d{2}[A-Z]{5}\d{4}[A-Z][0-9A-Z]{3}$/;

const TransportMaster = () => {
  const navigate = useNavigate();
  const [transports, setTransports] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [meta, setMeta] = useState({ states: [], categories: [] });
  const [rebuilding, setRebuilding] = useState(false);

  const [showDialog, setShowDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editingTransport, setEditingTransport] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  // customer tagging search
  const [custQuery, setCustQuery] = useState('');
  const [custResults, setCustResults] = useState([]);

  const fetchTransports = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter) params.append('status', statusFilter);
      const response = await apiClient.get(`/transport/list?${params}`);
      setTransports(response.data.transports || []);
    } catch (error) {
      toast.error('Failed to load transports');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMeta = async () => {
    try {
      const res = await apiClient.get('/transport/meta');
      setMeta({ states: res.data.states || [], categories: res.data.categories || [] });
    } catch { /* non-fatal */ }
  };

  useEffect(() => { fetchTransports(); }, [statusFilter]);
  useEffect(() => { fetchMeta(); }, []);

  // debounced customer search
  useEffect(() => {
    if (!custQuery.trim()) { setCustResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await apiClient.get(`/transport/customer-search?q=${encodeURIComponent(custQuery.trim())}`);
        setCustResults(res.data.customers || []);
      } catch { setCustResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [custQuery]);

  const openCreateDialog = () => {
    setFormData(emptyForm);
    setIsEditing(false); setEditingTransport(null);
    setCustQuery(''); setCustResults([]);
    setShowDialog(true);
  };

  const openEditDialog = (t) => {
    setFormData({
      name: t.name || '', contact_person: t.contact_person || '', contact_phone: t.contact_phone || '',
      address: t.address || '', status: t.status || 'active', ewb_transporter_id: t.ewb_transporter_id || '',
      billing_mode: t.billing_mode || 'per_kg',
      kg_per_box: t.kg_per_box ?? '',
      default_rate: t.default_rate ?? '',
      states: t.states || [],
      category_rates: (t.category_rates || []).map((c) => ({ category: c.category, rate: c.rate })),
      customers: (t.customers || []).map((c) => (typeof c === 'string' ? { name: c, id: '', place: '', state: '' } : c)),
    });
    setIsEditing(true); setEditingTransport(t);
    setCustQuery(''); setCustResults([]);
    setShowDialog(true);
  };

  const handleRebuildTat = async () => {
    setRebuilding(true);
    try {
      const res = await apiClient.post('/transport/rebuild-tat');
      const d = res.data;
      if (d.skipped_no_data) {
        toast.warning(d.message || 'No TAT / freight history found. Existing stats kept unchanged.');
      } else {
        toast.success(d.message || `Rebuilt ${d.tat_rows} TAT + ${d.rate_rows} freight-rate rows from ${d.dc_count} DCs`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to rebuild TAT & rates');
    } finally { setRebuilding(false); }
  };

  const addState = (st) => {
    if (!st || formData.states.includes(st)) return;
    setFormData((p) => ({ ...p, states: [...p.states, st] }));
  };
  const removeState = (st) => setFormData((p) => ({ ...p, states: p.states.filter((s) => s !== st) }));

  const addCategoryRate = () => setFormData((p) => ({ ...p, category_rates: [...p.category_rates, { category: '', rate: '' }] }));
  const updateCategoryRate = (i, field, val) => setFormData((p) => {
    const rows = [...p.category_rates]; rows[i] = { ...rows[i], [field]: val }; return { ...p, category_rates: rows };
  });
  const removeCategoryRate = (i) => setFormData((p) => ({ ...p, category_rates: p.category_rates.filter((_, idx) => idx !== i) }));

  const addCustomer = (cust) => {
    const nm = (cust && (cust.name || cust)) || '';
    const cid = (cust && cust.id) || '';
    if (!nm || formData.customers.some((c) => (c.name || c) === nm && (c.id || '') === cid)) return;
    setFormData((p) => ({
      ...p,
      customers: [...p.customers, { name: nm, id: cid, place: cust.place || '', state: cust.state || '' }],
    }));
    setCustQuery(''); setCustResults([]);
  };
  const removeCustomer = (cust) => {
    const nm = cust.name || cust; const cid = cust.id || '';
    setFormData((p) => ({ ...p, customers: p.customers.filter((c) => !((c.name || c) === nm && (c.id || '') === cid)) }));
  };

  const handleSave = async () => {
    if (!formData.name.trim()) { toast.error('Transport name is required'); return; }
    const tid = (formData.ewb_transporter_id || '').replace(/\s+/g, '').toUpperCase();
    if (tid && !TRANS_ID_RX.test(tid)) { toast.error('Transporter ID must be the 15-character GSTIN / TRANSIN (e.g. 33AABCT1234F1Z5)'); return; }
    if (formData.billing_mode === 'per_box' && !(parseFloat(formData.kg_per_box) > 0)) {
      toast.error('Per-box transports need a Kg-per-box value (> 0)'); return;
    }
    const category_rates = formData.category_rates
      .filter((c) => (c.category || '').trim() && c.rate !== '' && !isNaN(parseFloat(c.rate)))
      .map((c) => ({ category: c.category.trim(), rate: parseFloat(c.rate) }));

    const payload = {
      name: formData.name, contact_person: formData.contact_person, contact_phone: formData.contact_phone,
      address: formData.address, status: formData.status, ewb_transporter_id: tid,
      billing_mode: formData.billing_mode,
      kg_per_box: formData.kg_per_box === '' ? null : parseFloat(formData.kg_per_box),
      default_rate: formData.default_rate === '' ? null : parseFloat(formData.default_rate),
      states: formData.states,
      category_rates,
      customers: formData.customers,
    };

    setIsSaving(true);
    try {
      if (isEditing) {
        await apiClient.put(`/transport/update/${editingTransport.transport_id}`, payload);
        toast.success('Transport updated');
      } else {
        await apiClient.post('/transport/create', payload);
        toast.success('Transport created');
      }
      setShowDialog(false); fetchTransports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save transport');
    } finally { setIsSaving(false); }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete "${t.name}"?`)) return;
    try {
      await apiClient.delete(`/transport/delete/${t.transport_id}`);
      toast.success('Transport deleted/deactivated'); fetchTransports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete transport');
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="transport-master">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/sections/dispatch')} className="text-[#F9FAFB] hover:bg-white/10" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display flex items-center gap-2">
                <Truck className="w-6 h-6" /> Transport Master
              </h1>
              <p className="text-sm text-[#E8DCC8]">Manage transports, freight rules & auto-selection</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={handleRebuildTat} disabled={rebuilding} variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20" data-testid="rebuild-tat-btn">
              {rebuilding ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Clock className="w-4 h-4 mr-2" />}
              Rebuild TAT &amp; Rates
            </Button>
            <Button onClick={openCreateDialog} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="add-transport-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Transport
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <Card className="mb-6 border-[#E5DFD0]">
          <CardContent className="p-4">
            <div className="flex gap-3 flex-wrap">
              <Input placeholder="Search by name..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && fetchTransports()} className="flex-1 min-w-[200px]" data-testid="search-input" />
              <Select value={statusFilter || 'all'} onValueChange={(val) => setStatusFilter(val === 'all' ? '' : val)}>
                <SelectTrigger className="w-[150px]"><SelectValue placeholder="All Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={fetchTransports} variant="outline" data-testid="search-btn"><Search className="w-4 h-4 mr-2" /> Search</Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#E5DFD0]">
          <CardHeader className="pb-3"><CardTitle className="text-[#1A1F2E]">Transport Companies</CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-semibold">Transport Name</TableHead>
                    <TableHead className="font-semibold">Transporter ID</TableHead>
                    <TableHead className="font-semibold">Billing</TableHead>
                    <TableHead className="font-semibold">Rates / Box</TableHead>
                    <TableHead className="font-semibold">States</TableHead>
                    <TableHead className="font-semibold">Tagged Customers</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-8"><RefreshCw className="w-6 h-6 animate-spin mx-auto text-gray-400" /></TableCell></TableRow>
                  ) : transports.length === 0 ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-500"><Truck className="w-12 h-12 mx-auto text-gray-300 mb-2" /><p>No transports found</p></TableCell></TableRow>
                  ) : (
                    transports.map((t) => (
                      <TableRow key={t.transport_id} className="hover:bg-gray-50" data-testid={`transport-row-${t.transport_id}`}>
                        <TableCell className="font-medium"><div className="flex items-center gap-2"><Truck className="w-4 h-4 text-gray-400" />{t.name}</div></TableCell>
                        <TableCell className="font-mono text-xs" data-testid={`transport-tid-${t.transport_id}`}>
                          {t.ewb_transporter_id || <Badge className="bg-amber-100 text-amber-800 font-sans" data-testid={`transport-tid-missing-${t.transport_id}`}>missing</Badge>}
                        </TableCell>
                        <TableCell>
                          <Badge className={(t.billing_mode === 'per_box') ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}>
                            {t.billing_mode === 'per_box' ? `Per Box${t.kg_per_box ? ` · ${t.kg_per_box}kg/box` : ''}` : 'Per Kg'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          {(t.category_rates || []).length > 0
                            ? `${t.category_rates.length} cat rate(s)`
                            : (t.default_rate != null ? `default ₹${t.default_rate}` : <span className="text-gray-400">—</span>)}
                        </TableCell>
                        <TableCell className="text-sm max-w-[220px]">
                          {(t.states || []).length ? (
                            <span className="text-gray-700">{t.states.slice(0, 3).join(', ')}{t.states.length > 3 ? ` +${t.states.length - 3}` : ''}</span>
                          ) : <span className="text-gray-400">—</span>}
                        </TableCell>
                        <TableCell className="text-sm max-w-[200px] truncate">
                          {(t.customers || []).length ? (
                            <span className="text-gray-700">{t.customers.slice(0, 2).map((c) => { const nm = c.name || c; return c.id ? `${nm} (${c.id})` : nm; }).join(', ')}{t.customers.length > 2 ? ` +${t.customers.length - 2}` : ''}</span>
                          ) : <span className="text-gray-400">—</span>}
                        </TableCell>
                        <TableCell>
                          <Badge className={t.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>{t.status === 'active' ? 'Active' : 'Inactive'}</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openEditDialog(t)} className="text-blue-600 hover:bg-blue-50" data-testid={`edit-${t.transport_id}`}><Edit2 className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(t)} className="text-red-600 hover:bg-red-50" data-testid={`delete-${t.transport_id}`}><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Truck className="w-5 h-5" />{isEditing ? 'Edit Transport' : 'Add New Transport'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium">Transport Name <span className="text-red-500">*</span></Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., Professional Courier, MSS" className="mt-1" data-testid="transport-name-input" />
              </div>
              <div>
                <Label className="text-sm font-medium">Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium">Transporter ID (GSTIN / TRANSIN)</Label>
              <Input value={formData.ewb_transporter_id} onChange={(e) => setFormData({ ...formData, ewb_transporter_id: e.target.value.toUpperCase() })}
                maxLength={15} placeholder="15 characters, e.g. 33AABCT1234F1Z5" className="mt-1 font-mono uppercase" data-testid="transport-tid-input" />
              <p className="text-xs text-gray-500 mt-1">Goes into Part B of Direct Sales e-way bills. Invoices whose transporter has no ID are held under E-Way Bills → Needs attention.</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium">Contact Person</Label>
                <Input value={formData.contact_person} onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label className="text-sm font-medium">Contact Phone</Label>
                <Input value={formData.contact_phone} onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })} className="mt-1" />
              </div>
            </div>

            {/* ---- Freight rules ---- */}
            <div className="rounded-lg border border-[#E5DFD0] bg-[#FFFBF5] p-3 space-y-3">
              <p className="text-sm font-semibold text-[#1A1F2E]">Freight Rules (fallback rate)</p>
              <p className="text-xs text-gray-500">Recommendations use the transporter's <b>real freight rate</b> mined from Customer Outward records (freight ÷ weight/pieces). These manual rates are used <b>only as a fallback</b> when a transporter has no freight history yet. Click "Rebuild TAT &amp; Rates" to refresh from live data.</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-sm">Billing Mode</Label>
                  <Select value={formData.billing_mode} onValueChange={(v) => setFormData({ ...formData, billing_mode: v })}>
                    <SelectTrigger className="mt-1" data-testid="billing-mode-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="per_kg">Per Kg</SelectItem>
                      <SelectItem value="per_box">Per Box</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {formData.billing_mode === 'per_box' && (
                  <div>
                    <Label className="text-sm">Kg per Box</Label>
                    <Input type="number" value={formData.kg_per_box} onChange={(e) => setFormData({ ...formData, kg_per_box: e.target.value })} placeholder="e.g., 30" className="mt-1" data-testid="kg-per-box-input" />
                  </div>
                )}
                <div>
                  <Label className="text-sm">Default Rate (₹)</Label>
                  <Input type="number" value={formData.default_rate} onChange={(e) => setFormData({ ...formData, default_rate: e.target.value })} placeholder={formData.billing_mode === 'per_box' ? '₹/box' : '₹/kg'} className="mt-1" data-testid="default-rate-input" />
                </div>
              </div>

              {/* Category rates */}
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Category-wise Rates ({formData.billing_mode === 'per_box' ? '₹/box' : '₹/kg'})</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addCategoryRate} data-testid="add-cat-rate-btn"><Plus className="w-3 h-3 mr-1" />Add</Button>
                </div>
                <div className="space-y-2 mt-2">
                  {formData.category_rates.length === 0 && <p className="text-xs text-gray-400">No category rates — the Default Rate will be used for all categories.</p>}
                  {formData.category_rates.map((row, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`cat-rate-row-${i}`}>
                      <Select value={row.category || ''} onValueChange={(v) => updateCategoryRate(i, 'category', v)}>
                        <SelectTrigger className="flex-1" data-testid={`cat-rate-cat-${i}`}><SelectValue placeholder="Category" /></SelectTrigger>
                        <SelectContent>
                          {meta.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Input type="number" value={row.rate} onChange={(e) => updateCategoryRate(i, 'rate', e.target.value)} placeholder="Rate" className="w-28" data-testid={`cat-rate-val-${i}`} />
                      <Button type="button" size="sm" variant="ghost" className="text-red-600" onClick={() => removeCategoryRate(i)}><X className="w-4 h-4" /></Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Served states */}
              <div>
                <Label className="text-sm">Served States</Label>
                <Select value="" onValueChange={addState}>
                  <SelectTrigger className="mt-1" data-testid="add-state-select"><SelectValue placeholder="+ Add a state" /></SelectTrigger>
                  <SelectContent>
                    {meta.states.filter((s) => !formData.states.includes(s)).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="flex flex-wrap gap-1.5 mt-2" data-testid="states-chips">
                  {formData.states.map((s) => (
                    <span key={s} className="inline-flex items-center gap-1 bg-[#2D5F4F]/10 text-[#2D5F4F] rounded px-2 py-0.5 text-xs">
                      {s}<button onClick={() => removeState(s)} className="hover:text-red-600"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Customer tagging */}
            <div className="rounded-lg border border-[#E5DFD0] p-3 space-y-2">
              <Label className="text-sm font-semibold text-[#1A1F2E]">Tagged Customers (hard default)</Label>
              <p className="text-xs text-gray-500">If a customer is tagged here, this transport (among their tagged ones) is chosen by default for that customer.</p>
              <div className="relative">
                <Input value={custQuery} onChange={(e) => setCustQuery(e.target.value)} placeholder="Search customer by name, ID or place..." data-testid="customer-search-input" />
                {custResults.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white border rounded shadow max-h-48 overflow-y-auto">
                    {custResults.map((c) => (
                      <button key={`${c.id}-${c.name}`} onClick={() => addCustomer(c)} className="block w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100" data-testid={`cust-result-${c.name}`}>
                        <span className="font-medium text-[#1A1F2E]">{c.name}</span>
                        {c.id ? <span className="text-indigo-500"> · ID {c.id}</span> : ''}
                        {c.place ? <span className="text-gray-400"> · {c.place}</span> : ''}
                        {c.state ? <span className="text-gray-400"> · {c.state}</span> : ''}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5" data-testid="customers-chips">
                {formData.customers.map((c) => {
                  const nm = c.name || c;
                  const cid = c.id || '';
                  return (
                    <span key={`${cid}|${nm}`} className="inline-flex items-center gap-1 bg-indigo-100 text-indigo-700 rounded px-2 py-0.5 text-xs" data-testid={`customer-chip-${nm}`}>
                      <span>{nm}{cid ? <span className="text-indigo-500 font-medium"> · ID {cid}</span> : ''}{c.place ? <span className="text-indigo-400"> · {c.place}</span> : ''}</span>
                      <button onClick={() => removeCustomer(c)} className="hover:text-red-600"><X className="w-3 h-3" /></button>
                    </span>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium">Address</Label>
              <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} className="mt-1" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={isSaving} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="save-transport-btn">
              {isSaving ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Saving...</> : (isEditing ? 'Update' : 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TransportMaster;
